import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Power, Trash2, X, Users2 } from "lucide-react";
import api, { errMsg } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import CustomerContactsSites from "../components/CustomerContactsSites";
import { useAuth } from "../context/AuthContext";

const empty = { customerName: "", consigneeAddress: "", deliveryAddress: "", gstNumber: "", consigneeGstNumber: "", contactPerson: "", mobile: "", email: "" };

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const perms = user.permissions || [];
  const canAdd = isAdmin || perms.includes("add_customer");
  const canEdit = isAdmin || perms.includes("edit_customer");
  const canDelete = isAdmin || perms.includes("delete_customer");
  const canManage = canAdd || canEdit || canDelete;
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [manage, setManage] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/customers", { params: { search } }); setRows(data); };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openNew = () => { setForm(empty); setModal("new"); };
  const openEdit = (c) => { setForm(c); setModal(c.id); };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.customerName) return toast.error("Customer name is required.");
    try {
      if (modal === "new") await api.post("/customers", form);
      else await api.put(`/customers/${modal}`, form);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (c) => {
    try { await api.patch(`/customers/${c.id}/status`, null, { params: { status: c.status === "active" ? "inactive" : "active" } }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.customerName}"?`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Customer Master" subtitle={`${rows.length} customer(s)`}
        actions={canAdd && <button className="btn-accent" onClick={openNew} data-testid="add-customer-btn"><Plus className="h-4 w-4" /> Add Customer</button>} />
      <div className="p-6 lg:p-8 space-y-4">
        <input className="input max-w-sm" placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="customer-search" />
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200"><tr>
              <th className="th">Name</th><th className="th">Contact</th><th className="th">Mobile</th><th className="th">GST</th><th className="th">Status</th>{canManage && <th className="th">Actions</th>}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && <tr><td className="td text-slate-400" colSpan={6}>No customers.</td></tr>}
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="td font-semibold text-slate-900">{c.customerName}</td>
                  <td className="td">{c.contactPerson || "—"}</td>
                  <td className="td">{c.mobile || "—"}</td>
                  <td className="td font-mono text-xs">{c.gstNumber || "—"}</td>
                  <td className="td"><StatusBadge status={c.status} /></td>
                  {canManage && <td className="td"><div className="flex gap-1">
                    <button className="p-1.5 rounded hover:bg-amber-50" title="Contacts & Sites" onClick={() => setManage(c)} data-testid={`manage-customer-${c.id}`}><Users2 className="h-4 w-4 text-amber-600" /></button>
                    {canEdit && <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>}
                    {canEdit && <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => toggle(c)} data-testid={`toggle-customer-${c.id}`}><Power className={`h-4 w-4 ${c.status === "active" ? "text-green-600" : "text-slate-400"}`} /></button>}
                    {canDelete && <button className="p-1.5 rounded hover:bg-red-50" onClick={() => del(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>}
                  </div></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">{modal === "new" ? "Add Customer" : "Edit Customer"}</h3>
              <button onClick={() => setModal(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><label className="label">Customer Name *</label><input className="input" value={form.customerName} onChange={(e) => set("customerName", e.target.value)} data-testid="customer-name-input" /></div>
              <div><label className="label">Consignee's Address</label><textarea className="input h-20 py-2" value={form.consigneeAddress} onChange={(e) => set("consigneeAddress", e.target.value)} /></div>
              <div><label className="label">Delivery Address</label><textarea className="input h-20 py-2" value={form.deliveryAddress} onChange={(e) => set("deliveryAddress", e.target.value)} /></div>
              <div><label className="label">GST Number</label><input className="input" value={form.gstNumber} onChange={(e) => set("gstNumber", e.target.value)} /></div>
              <div><label className="label">Consignee's GST Number</label><input className="input" value={form.consigneeGstNumber} onChange={(e) => set("consigneeGstNumber", e.target.value)} /></div>
              <div><label className="label">Contact Person</label><input className="input" value={form.contactPerson} onChange={(e) => set("contactPerson", e.target.value)} /></div>
              <div><label className="label">Mobile</label><input className="input" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></div>
              <div><label className="label">Email</label><input className="input" value={form.email} onChange={(e) => set("email", e.target.value)} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button className="btn-outline" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={save} data-testid="save-customer-btn">Save</button>
            </div>
          </div>
        </div>
      )}
      {manage && <CustomerContactsSites customer={manage} onClose={() => setManage(null)} />}
    </div>
  );
}
